import { HFlow, SelectHelperMenuItem, Text, VFlow } from 'bold-ui'
import locale from 'bold-ui/lib/i18n/locales/pt-BR'
import { AsyncSelectQueryFieldProps, SelectField } from 'components/form'
import { HLabel } from 'components/HLabel'
import { MedicamentosDocument } from 'graphql/hooks.generated'
import { MedicamentosQuery, MedicamentosQueryVariables } from 'graphql/types.generated'
import React from 'react'

import { DEFAULT_SELECT_SIZE, useAsyncQuerySelect } from '../useAsyncQuerySelect'

export type MedicamentoSelectModel = MedicamentosQuery['medicamentos']['content'][0]

export interface MedicamentoSelectFieldProps
  extends AsyncSelectQueryFieldProps<MedicamentoSelectModel, MedicamentosQuery, MedicamentosQueryVariables> {
  loadItemsOnOpen?: boolean
}

const extractItems = (data: MedicamentosQuery) => {
  return data?.medicamentos?.content
}

const handleItemToString = (item: MedicamentoSelectModel) => {
  return item?.principioAtivo ? `${item.principioAtivo}` : ''
}

const handleRenderItem = (item: MedicamentoSelectModel) => {
  return (
    <VFlow vSpacing={0}>
      <Text fontWeight='bold'>{`${item.principioAtivo} ${item.concentracao ? item.concentracao : ''}`.trim()}</Text>
      <HFlow hSpacing={0.25}>
        <HLabel title='Forma farmacêutica'>{item.formaFarmaceutica.nome}</HLabel>
      </HFlow>
      <HFlow hSpacing={0.25}>
        <HLabel title='Unidade de fornecimento'>{item.unidadeFornecimento}</HLabel>
      </HFlow>
    </VFlow>
  )
}

const variables = (inputQuery: string): MedicamentosQueryVariables => ({
  input: {
    query: inputQuery,
    pageParams: {
      size: DEFAULT_SELECT_SIZE,
    },
  },
})

MedicamentoSelectField.defaultProps = {
  loadItemsOnOpen: true,
} as Partial<MedicamentoSelectFieldProps>

export function MedicamentoSelectField(props: MedicamentoSelectFieldProps) {
  const { loadItemsOnOpen, ...rest } = props
  const { skiping, ...asyncProps } = useAsyncQuerySelect<
    MedicamentoSelectModel,
    MedicamentosQuery,
    MedicamentosQueryVariables
  >({
    query: MedicamentosDocument,
    extractItems,
    variables,
    skip: (inputString) => (!loadItemsOnOpen ? inputString?.trim()?.length < 2 : undefined),
    debounceTime: 500,
  })

  return (
    <SelectField<MedicamentoSelectModel>
      itemToString={handleItemToString}
      renderItem={handleRenderItem}
      components={
        !loadItemsOnOpen && {
          EmptyItem: () => (
            <SelectHelperMenuItem>
              {skiping ? 'Digite para buscar um medicamento.' : locale.select.emptyItem}
            </SelectHelperMenuItem>
          ),
        }
      }
      {...asyncProps}
      {...rest}
    />
  )
}
